import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Button,
    Grid,
    Box,
    CircularProgress,
    FormLabel,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    TextField,
    TableContainer,
    Table,
    ListItemText,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { getMinimentalExamStatusById, gotoNextMenu, gotoPreviousMenu, saveMiniMentalExamStatus } from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import { copyObject, highlightStyle } from '../../../utils/common';
import { MiniMentalExamItem } from '../../../Models/MiniMentalExams/MiniMentalExamItem.model';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { MiniMentalExam } from '../../../Models/MiniMentalExams/MiniMentalExam.model';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { isAIIcon } from '../Assessment';
import SafetyPlanPromptModal from '../../SafetyPlanPromptModal/SafetyPlanPromptModal';
import { openSafetyPlanModal, setShowSafetyPlanModal } from '../../../store/behaviorOrSafetyPlanSlice.slice';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Legal_History_Mini_Mental_Exam.pdf';

export const DANGER_TO = {
    SELF: 'DTS',
    OTHERS: 'DTO',
};

const MiniMentalExamComponent: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { showSafetyPlanModal } = useSelector((state: RootState) => state.safetyPlanSlice);
    const { miniMentalExam, isFetching } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft?.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;

    const isComplete = (miniMentalExam: MiniMentalExam, isRequired: boolean) => {
        if (!miniMentalExam || typeof miniMentalExam !== 'object') {
            return false;
        }
        if (!isRequired) {
            return true;
        }
        const requiredCategories = ['GeneralObservation', 'Thought', 'Cognition', 'Emotion'];
        const requiredItems = {
            GeneralObservation: ['Appearance', 'Behavior', 'Speech'],
            Thought: ['DTS', 'DTO', 'ThoughtProcess'],
            Cognition: ['Orientation', 'MemoryIssues', 'AttentionConcentration', 'JudgmentInsight'],
            Emotion: ['Mood', 'Affect'],
        };
        const missingItems: string[] = [];
        for (const category of requiredCategories) {
            const categoryData = miniMentalExam[category];
            if (!categoryData || typeof categoryData !== 'object') {
                missingItems.push(category);
                continue;
            }
            const items = requiredItems[category];
            if (!items || items.length === 0) {
                missingItems.push(category);
                continue;
            }
            for (const itemName of items) {
                const item = categoryData[itemName] as MiniMentalExamItem;
                if (!item || (item.Types === null && item.Type === null)) {
                    missingItems.push(`${category} - ${itemName}`);
                }
            }
        }
        if (missingItems.length > 0) {
            return false;
        }
        return true;
    };

    const miniMentalExamsByType = [
        {
            type: 'GeneralObservation',
            name: 'General Observation',
            list: [
                { name: 'Build', isMultiSelect: false, isRequired: false },
                { name: 'Appearance', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'Demeanor', isMultiSelect: true, isRequired: false },
                { name: 'EyeContact', isMultiSelect: false, isRequired: false },
                { name: 'Behavior', isMultiSelect: true, isRequired: isBHDiagnostic },
                { name: 'ActivityLevel', isMultiSelect: false, isRequired: false },
                { name: 'Speech', isMultiSelect: true, isRequired: isBHDiagnostic },
            ],
        },
        {
            type: 'Thought',
            name: 'Thought',
            list: [
                { name: 'ThoughtProcess', isMultiSelect: true, isRequired: isBHDiagnostic },
                { name: 'Anxiety', isMultiSelect: true, isRequired: false },
                { name: 'EstimatedIntelligence', isMultiSelect: false, isRequired: false },
                { name: 'Delusions', isMultiSelect: true, isRequired: false },
                { name: 'DelusionsOther', isMultiSelect: true, isRequired: false },
                { name: 'Hallucinations', isMultiSelect: true, isRequired: false },
                { name: 'HallucinationsOther', isMultiSelect: true, isRequired: false },
                { name: 'Perceptions', isMultiSelect: false, isRequired: false },
                { name: 'DTS', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'DTO', isMultiSelect: false, isRequired: isBHDiagnostic },
            ],
        },
        {
            type: 'Cognition',
            name: 'Cognition',
            list: [
                { name: 'Orientation', isMultiSelect: true, isRequired: isBHDiagnostic },
                { name: 'MemoryIssues', isMultiSelect: true, isRequired: isBHDiagnostic },
                { name: 'AttentionConcentration', isMultiSelect: false, isRequired: isBHDiagnostic },
                { name: 'JudgmentInsight', isMultiSelect: true, isRequired: isBHDiagnostic },
            ],
        },
        {
            type: 'Emotion',
            name: 'Emotion',
            list: [
                { name: 'Mood', isMultiSelect: true, isRequired: isBHDiagnostic },
                { name: 'Affect', isMultiSelect: false, isRequired: isBHDiagnostic },
            ],
        },
    ];

    useEffect(() => {
        dispatch(getMinimentalExamStatusById(engagementDraft.MiniMentalExamId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnChangeMiniMentalExamItem = (value: any, typePropsName: string, parentPropsName: string, childPropsName: string, saveLocal = false) => {
        let data = copyObject(miniMentalExam);
        data[typePropsName][parentPropsName][childPropsName] = value;
        if (data[typePropsName][parentPropsName]) {
            data[typePropsName][parentPropsName].IsAIAnswer = false;
        }
        dispatch(saveMiniMentalExamStatus({ ...data, MemberId: memberId }, saveLocal));
    };

    const handleOnClickSave = () => {
        if (isSafetyPlanTriggered()) {
            dispatch(openSafetyPlanModal());
            return;
        }
        setShowSaveAlert(true);
    };

    const handleSafetyPlanModalComplete = () => {
        dispatch(setShowSafetyPlanModal(false));
        setShowSaveAlert(true);
    };

    const isSafetyPlanTriggered = (): boolean => {
        const dtsName: string = miniMentalExam?.Thought?.DTS?.Type?.Name ?? '';
        const dtoName: string = miniMentalExam?.Thought?.DTO?.Type?.Name ?? '';
        return dtsName.includes('Yes') || dtoName.includes('Yes');
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            dispatch(saveMiniMentalExamStatus({ ...miniMentalExam, Status: StatusEnum.Completed }, false, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    const renderMiniMentalExamItem = (typePropsName: string, parentPropsName: string, index: number, isMultiSelect = false, isRequired = false) => {
        const typeData = miniMentalExam[typePropsName];
        if (!Boolean(typeData)) {
            return null;
        }

        const miniMentalExamItem = typeData[parentPropsName] as MiniMentalExamItem;
        const typeList = miniMentalExamItem?.TypeList || [];

        return (
            Boolean(miniMentalExamItem) && (
                <TableRow key={`general-observ-${parentPropsName}-${index}`} sx={{ width: '100%', '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell width="25%" style={{ verticalAlign: 'top' }}>
                        <FormLabel sx={isBHDiagnostic && isRequired ? highlightStyle : undefined} required={isBHDiagnostic && isRequired}>
                            {miniMentalExamItem.Name}
                            {miniMentalExamItem.IsAIAnswer && isAIIcon(miniMentalExamItem)}
                        </FormLabel>
                    </TableCell>
                    <TableCell width="75%">
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel required={isBHDiagnostic && isRequired} id={`${parentPropsName}-type-select-label`}>
                                        Type
                                    </InputLabel>
                                    {isMultiSelect ? (
                                        <Select
                                            required={isBHDiagnostic && isRequired}
                                            multiple={isMultiSelect}
                                            error={isBHDiagnostic && isRequired && !miniMentalExamItem?.Types?.length}
                                            labelId={`${parentPropsName}-type-select-label`}
                                            value={Boolean(miniMentalExamItem?.Types?.length) ? miniMentalExamItem.Types.map((t) => t.Id) : []}
                                            label="Type"
                                            renderValue={(selected) =>
                                                typeList
                                                    .filter((t) => selected.some((s) => s === t.Id))
                                                    .map((m) => m.Name)
                                                    .join(', ')
                                            }
                                            onChange={(e) =>
                                                handleOnChangeMiniMentalExamItem(
                                                    typeList.filter((t) => (e.target.value as string[])?.some((v) => v === t.Id)),
                                                    typePropsName,
                                                    parentPropsName,
                                                    'Types'
                                                )
                                            }
                                        >
                                            {typeList.map((type) => (
                                                <MenuItem key={type.Id} value={type.Id}>
                                                    <Checkbox
                                                        checked={Boolean(miniMentalExamItem.Types) && miniMentalExamItem.Types.some((t) => t.Id === type.Id)}
                                                    />
                                                    <ListItemText primary={type.Name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Select
                                            required={isBHDiagnostic && isRequired}
                                            labelId={`${parentPropsName}-type-select-label`}
                                            value={Boolean(miniMentalExamItem?.Type?.Id) ? miniMentalExamItem.Type.Id : ''}
                                            error={isBHDiagnostic && isRequired && !miniMentalExamItem?.Type?.Id}
                                            label="Type"
                                            onChange={(e) =>
                                                handleOnChangeMiniMentalExamItem(
                                                    miniMentalExamItem.TypeList.find((t) => t.Id === e.target.value),
                                                    typePropsName,
                                                    parentPropsName,
                                                    'Type'
                                                )
                                            }
                                        >
                                            {miniMentalExamItem?.TypeList?.map((type) => (
                                                <MenuItem key={type.Id} value={type.Id}>
                                                    {type.Name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                                <TextField
                                    label="Comments"
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    size="small"
                                    value={Boolean(miniMentalExamItem?.Description) ? miniMentalExamItem.Description : ''}
                                    onChange={(e) => handleOnChangeMiniMentalExamItem(e.target.value, typePropsName, parentPropsName, 'Description', true)}
                                    onBlur={(e) => handleOnChangeMiniMentalExamItem(e.target.value, typePropsName, parentPropsName, 'Description')}
                                />
                            </Grid>
                        </Grid>
                    </TableCell>
                </TableRow>
            )
        );
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                <Typography variant="h6">Mini Mental Exam</Typography>
                <Tooltip title="Mini Mental Exam Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            {isFetching ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid className="content-form">
                        {miniMentalExamsByType.map((type, index) => (
                            <Grid container key={`mini-mental-exam-item-${index}`}>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <Typography variant="h6" component="label">
                                        {type.name}
                                    </Typography>
                                    {isBHDiagnostic && (
                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                            * Required for BH Diagnostic
                                        </Typography>
                                    )}
                                    <hr />
                                </Grid>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <TableContainer sx={{ width: '100%' }}>
                                        <Table sx={{ width: '100%' }}>
                                            <TableBody>
                                                {type.list.map((item, itemIndex) =>
                                                    renderMiniMentalExamItem(type.type, item.name, itemIndex, item.isMultiSelect, item.isRequired)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                            Next
                        </Button>
                    </Grid>
                </>
            )}
            {Boolean(isComplete(miniMentalExam, isBHDiagnostic) && isBHDiagnostic) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Mini Mental Exam Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    showBottomCenter={true}
                    message="I confirm and acknowledge that Mini Mental Exam assessment is completed."
                />
            ) : Boolean(engagementDraft.MiniMentalExamId && !isBHDiagnostic) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Mini Mental Exam Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    showBottomCenter={true}
                    message="I confirm and acknowledge that Mini Mental Exam assessment is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Mini Mental Exam Assessment"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    showBottomCenter={true}
                    onClose={() => setShowSaveAlert(false)}
                    renderBody={() =>
                        BHDiagnosticConfirmationBody({
                            assessmentName: 'Mini Mental Exam',
                            diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                        })
                    }
                />
            )}
            <SafetyPlanPromptModal
                open={showSafetyPlanModal}
                onCancel={() => dispatch(setShowSafetyPlanModal(false))}
                onComplete={handleSafetyPlanModalComplete}
            />
        </Grid>
    );
};

export default MiniMentalExamComponent;
